/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';
import {
  Routes,
  Route,
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Button,
  Table,
  Stack,
  Spinner,
  Alert,
  Form,
  Modal,
  Row,
  Col,
  Pagination,
  OverlayTrigger,
  Tooltip,
  Dropdown,
} from 'react-bootstrap';
import {
  FaTrash,
  FaEdit,
  FaEllipsisV,
  FaPlusCircle,
  FaFilePdf,
} from 'react-icons/fa';
import {VscSparkleFilled} from 'react-icons/vsc';
import {LuFileJson} from 'react-icons/lu';
import {RxDragHandleDots2} from 'react-icons/rx';
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
  FloatingFocusManager,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
} from '@floating-ui/react';
import {jsPDF as JsPDF} from 'jspdf';
import {toPng} from 'html-to-image';
import JobForm from '../models/job-form';
import ResumeForm, {validateResumePayload} from '../models/resume-form';
import type {
  JobDescriptionDbModel,
  ResumeDbModel,
  JobDescriptionListInfo,
  ResumeListInfo,
} from '../models/database-models';
import type {JobDescriptionSchema} from '../models/job';
import {
  aiResumeStrictnessConst as StrictnessOptions,
  resumeThemesNew,
} from '../models/other';
import {BasicAiAlert} from '../libs/alert';
import {track} from '../libs/analytics';
import {useAppDispatch} from '../store/hooks';
import {
  setHasTailored,
  setHasJob,
  setHasResume,
} from '../store/quickstart-slice';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringOption(
  value: unknown,
  options: readonly string[],
): value is string {
  return typeof value === 'string' && options.includes(value);
}

function formatFastApiDetail(data: unknown): string | undefined {
  if (!isRecord(data)) {
    return undefined;
  }

  const {detail} = data as {detail?: unknown};
  if (typeof detail === 'string') {
    return detail;
  }

  if (!Array.isArray(detail)) {
    return undefined;
  }

  const messages: string[] = [];
  for (const item of detail) {
    if (typeof item === 'string') {
      messages.push(item);
      continue;
    }

    if (!isRecord(item)) {
      continue;
    }

    const message = typeof item.msg === 'string' ? item.msg : undefined;
    if (!message) {
      continue;
    }

    if (Array.isArray(item.loc)) {
      const loc = item.loc
        .map(part =>
          typeof part === 'string' || typeof part === 'number'
            ? String(part)
            : '',
        )
        .filter(Boolean)
        .join('.');
      messages.push(loc ? `${loc}: ${message}` : message);
      continue;
    }

    messages.push(message);
  }

  if (messages.length > 0) {
    return messages.join('; ');
  }

  return undefined;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (isRecord(error)) {
    const {response} = error as {response?: unknown};
    if (isRecord(response)) {
      const {data} = response as {data?: unknown};
      const detailMessage = formatFastApiDetail(data);
      if (detailMessage) {
        return detailMessage;
      }

      if (typeof data === 'string') {
        return data;
      }

      if (isRecord(data) && typeof data.message === 'string') {
        return data.message;
      }
    }

    if (typeof error.message === 'string') {
      return error.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
}

function ResizableSplit({
  children,
  initialLeftPercent = 50,
}: {
  children: React.ReactNode;
  initialLeftPercent?: number;
}): React.JSX.Element {
  const [leftNode, rightNode] = React.Children.toArray(children);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const draggingRef = React.useRef(false);
  const dragStartPointRef = React.useRef(0);
  const startLeftRef = React.useRef(0);

  const [leftPercent, setLeftPercent] =
    React.useState<number>(initialLeftPercent);

  React.useEffect(() => {
    const onMove = (clientX: number) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();
      const total = rect.width;
      const delta = clientX - dragStartPointRef.current;
      const newLeftPx = (startLeftRef.current / 100) * total + delta;
      let percent = (newLeftPx / total) * 100;
      percent = Math.max(10, Math.min(90, percent)); // Clamp
      setLeftPercent(Math.round(percent));
    };

    const onMouseMove = (event: MouseEvent) => {
      if (!draggingRef.current) {
        return;
      }

      onMove(event.clientX);
    };

    const onMouseUp = () => {
      draggingRef.current = false;
      document.body.style.userSelect = '';
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!draggingRef.current) {
        return;
      }

      onMove(event.touches[0].clientX);
    };

    const onTouchEnd = () => {
      draggingRef.current = false;
      document.body.style.userSelect = '';
    };

    globalThis.addEventListener('mousemove', onMouseMove);
    globalThis.addEventListener('mouseup', onMouseUp);
    globalThis.addEventListener('touchmove', onTouchMove, {passive: false});
    globalThis.addEventListener('touchend', onTouchEnd);

    return () => {
      globalThis.removeEventListener('mousemove', onMouseMove);
      globalThis.removeEventListener('mouseup', onMouseUp);
      globalThis.removeEventListener('touchmove', onTouchMove);
      globalThis.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  const onDividerMouseDown = (clientX: number) => {
    draggingRef.current = true;
    dragStartPointRef.current = clientX;
    startLeftRef.current = leftPercent;
    document.body.style.userSelect = 'none';
  };

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        width: '100%',
        alignItems: 'stretch',
        gap: 8,
      }}
    >
      <div
        style={{
          width: `${leftPercent}%`,
          minWidth: 160,
          overflow: 'auto',
        }}
      >
        {leftNode}
      </div>

      <div
        role='separator'
        aria-orientation='vertical'
        aria-label='Resize panels'
        onMouseDown={event => {
          onDividerMouseDown(event.clientX);
        }}
        onTouchStart={event => {
          onDividerMouseDown(event.touches[0].clientX);
        }}
        style={{
          width: 16,
          cursor: 'col-resize',
          position: 'relative',
          flex: '0 0 auto',
          borderLeft: '2px solid #dee2e6',
          backgroundClip: 'padding-box',
          background: '#eff0f3',
        }}
      >
        <RxDragHandleDots2
          size={16}
          color='#6c757d'
          aria-hidden
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            opacity: 0.9,
          }}
        />
      </div>

      <div
        style={{
          flex: 1,
          overflow: 'auto',
        }}
      >
        {rightNode}
      </div>
    </div>
  );
}

type GenerateModalProps = {
  show: boolean;
  onHide: () => void;
  job?: JobDescriptionDbModel | undefined;
};

function GenerateResumeModal({
  show,
  onHide,
  job,
}: GenerateModalProps): React.JSX.Element {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [resumes, setResumes] = React.useState<ResumeListInfo[]>([]);
  const [loadingResumes, setLoadingResumes] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [selectedResumeId, setSelectedResumeId] = React.useState<string>('');
  const [generating, setGenerating] = React.useState(false);
  const [generated, setGenerated] = React.useState<ResumeDbModel | undefined>(
    undefined,
  );
  const [strictness, setStrictness] = React.useState<string>(
    StrictnessOptions[1],
  );
  const [inheritedTheme, setInheritedTheme] = React.useState<
    string | undefined
  >(undefined);

  React.useEffect(() => {
    if (!show) {
      return;
    }

    let mounted = true;
    (async () => {
      try {
        setLoadingResumes(true);
        setError(undefined);
        const response = await axios.get<ResumeListInfo[]>('/api/resumes');
        const {data} = response;
        // Defensive: ensure we always set an array. Some servers may return an object instead of an array.
        if (mounted) {
          setResumes(Array.isArray(data) ? data : []);
        }
      } catch (error_: unknown) {
        if (mounted) {
          setError(getErrorMessage(error_, 'Failed to load resumes'));
        }
      } finally {
        if (mounted) {
          setLoadingResumes(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [show]);

  React.useEffect(() => {
    if (!show) {
      // Reset modal state when hidden
      setSelectedResumeId('');
      setGenerated(undefined);
      setStrictness(StrictnessOptions[1]);
      setInheritedTheme(undefined);
      setError(undefined);
    }
  }, [show]);

  const handleGenerate = async () => {
    if (!job) {
      setError('No job selected');
      return;
    }

    const selId = selectedResumeId;
    const sel = resumes.find(r => r.id === selId);
    if (!sel || !selId) {
      setError('Please select an existing resume');
      return;
    }

    // Fetch the full resume model before generating
    const resumeResponse = await axios.get<ResumeDbModel>(
      `/api/resumes/${selId}`,
    );
    const resumeModel = resumeResponse.data;

    // Inherit the base resume's theme for the generated draft
    try {
      const key = `resume_theme_${selId}`;
      const saved =
        localStorage.getItem(key) ??
        localStorage.getItem('resume_theme_global');
      if (isStringOption(saved, resumeThemesNew)) {
        setInheritedTheme(saved);
      } else {
        setInheritedTheme(undefined);
      }
    } catch {
      setInheritedTheme(undefined);
    }

    try {
      setGenerating(true);
      setError(undefined);
      const response = await axios.post<ResumeDbModel>(
        '/api/ai/resume_from_job_and_resume',
        {job_model: job, resume_model: resumeModel},
        {params: {strictness}},
      );
      setGenerated(response.data);
      track('ai_resume_created', {method: 'job+resume', strictness});
      try {
        void dispatch(setHasTailored(true));
      } catch {}
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to generate resume'));
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async (value: ResumeDbModel) => {
    const validationErrors = validateResumePayload(value);
    if (validationErrors.summary.length > 0) {
      setError(
        `Fix these resume format issues: ${validationErrors.summary.join(' ')}`,
      );
      return;
    }

    try {
      setGenerating(true);
      setError(undefined);
      const response = await axios.post<ResumeDbModel>('/api/resumes', value);
      const created = response.data;
      try {
        if (created?.id && inheritedTheme) {
          localStorage.setItem(`resume_theme_${created.id}`, inheritedTheme);
        }
      } catch {}

      try {
        void dispatch(setHasResume(true));
      } catch {}

      onHide();
      if (created?.id === undefined || created?.id === null) {
        void navigate('/resumes');
      } else {
        void navigate(`/resumes/${created.id}`);
      }
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to save resume'));
    } finally {
      setGenerating(false);
    }
  };

  const handleDiscard = () => {
    setGenerated(undefined);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>Generate Resume from Job</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant='danger'>{error}</Alert>}

        {!generated && (
          <>
            <Row className='mb-3'>
              <Col>
                <Form.Label>
                  Select an existing resume to use as a base
                </Form.Label>
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col>
                {loadingResumes ? (
                  <div className='d-flex align-items-center'>
                    <Spinner animation='border' size='sm' className='me-2' />
                    <span>Loading resumes...</span>
                  </div>
                ) : (
                  <Form.Select
                    value={selectedResumeId}
                    onChange={event => {
                      setSelectedResumeId(
                        (event.target as HTMLSelectElement).value,
                      );
                    }}
                  >
                    <option value=''>-- select a resume --</option>
                    {resumes.map(r => (
                      <option key={r.id ?? r.name} value={r.id ?? ''}>
                        {r.name ?? `Resume ${r.id ?? ''}`}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col md={6}>
                <Form.Label>Strictness</Form.Label>
                <Form.Select
                  value={strictness}
                  onChange={event => {
                    setStrictness((event.target as HTMLSelectElement).value);
                  }}
                >
                  {StrictnessOptions.map(s => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            <BasicAiAlert />

            <div className='d-flex justify-content-end'>
              <Button
                variant='secondary'
                className='me-2'
                onClick={onHide}
                disabled={generating}
              >
                Cancel
              </Button>
              <Button
                disabled={generating}
                onClick={() => {
                  void handleGenerate();
                }}
              >
                {generating ? 'Generating...' : 'Generate'}
              </Button>
              {generating && <Spinner animation='border' className='ms-2' />}
            </div>
          </>
        )}

        {generated && (
          <>
            <Alert variant='info'>
              AI-generated resume — review and edit before saving.
            </Alert>
            <ResumeForm
              value={generated}
              onChange={v => {
                setGenerated(v);
              }}
              onSubmit={v => {
                void handleSave(v);
              }}
              readOnly={false}
              showActions={true}
              showTopActions={true}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {generated ? (
          <>
            <Button
              variant='outline-secondary'
              onClick={() => {
                setGenerated(undefined);
              }}
              disabled={generating}
            >
              Back
            </Button>
            <Button
              variant='secondary'
              onClick={handleDiscard}
              disabled={generating}
            >
              Discard
            </Button>
          </>
        ) : (
          <Button variant='secondary' onClick={onHide} disabled={generating}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

const aiResumeFromJobTextSettingsKey = 'ai_resume_from_jobtext_settings';

type AiResumeFromJobTextResponse = {
  resume: ResumeDbModel;
  job: JobDescriptionDbModel;
};

type GenerateResumeFromJobTextModalProps = {
  show: boolean;
  onHide: () => void;
  jobText: string;
};

function GenerateResumeFromJobTextModal({
  show,
  onHide,
  jobText,
}: GenerateResumeFromJobTextModalProps): React.JSX.Element {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [resumes, setResumes] = React.useState<ResumeListInfo[]>([]);
  const [loadingResumes, setLoadingResumes] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [selectedResumeId, setSelectedResumeId] = React.useState<string>('');
  const [strictness, setStrictness] = React.useState<string>(
    StrictnessOptions[1],
  );
  const [reviewBeforeSaving, setReviewBeforeSaving] =
    React.useState<boolean>(true);
  const [saveGeneratedJob, setSaveGeneratedJob] =
    React.useState<boolean>(false);
  const [generating, setGenerating] = React.useState(false);
  const [generated, setGenerated] = React.useState<ResumeDbModel | undefined>(
    undefined,
  );
  const [generatedJob, setGeneratedJob] = React.useState<
    JobDescriptionDbModel | undefined
  >(undefined);
  const [inheritedTheme, setInheritedTheme] = React.useState<
    string | undefined
  >(undefined);

  // Load persisted settings once on mount
  React.useEffect(() => {
    try {
      const saved = localStorage.getItem(aiResumeFromJobTextSettingsKey);
      if (saved) {
        const parsed = JSON.parse(saved) as unknown;
        if (!isRecord(parsed)) {
          return;
        }

        if (typeof parsed.selectedResumeId === 'string') {
          setSelectedResumeId(parsed.selectedResumeId);
        }

        if (isStringOption(parsed.strictness, StrictnessOptions)) {
          setStrictness(parsed.strictness);
        }

        if (typeof parsed.reviewBeforeSaving === 'boolean') {
          setReviewBeforeSaving(parsed.reviewBeforeSaving);
        }

        if (typeof parsed.saveGeneratedJob === 'boolean') {
          setSaveGeneratedJob(parsed.saveGeneratedJob);
        }
      }
    } catch {}
  }, []);

  // Fetch resume list when modal opens
  React.useEffect(() => {
    if (!show) {
      return;
    }

    let mounted = true;
    (async () => {
      try {
        setLoadingResumes(true);
        setError(undefined);
        const response = await axios.get<ResumeListInfo[]>('/api/resumes');
        const {data} = response;
        if (mounted) {
          setResumes(Array.isArray(data) ? data : []);
        }
      } catch (error_: unknown) {
        if (mounted) {
          setError(getErrorMessage(error_, 'Failed to load resumes'));
        }
      } finally {
        if (mounted) {
          setLoadingResumes(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [show]);

  // Reset transient state when modal hides (keep settings)
  React.useEffect(() => {
    if (!show) {
      setGenerated(undefined);
      setGeneratedJob(undefined);
      setInheritedTheme(undefined);
      setError(undefined);
    }
  }, [show]);

  const saveSettings = () => {
    try {
      localStorage.setItem(
        aiResumeFromJobTextSettingsKey,
        JSON.stringify({
          selectedResumeId,
          strictness,
          reviewBeforeSaving,
          saveGeneratedJob,
        }),
      );
    } catch {}
  };

  const handleSave = async (
    value: ResumeDbModel,
    theme?: string,
    jobToSave?: JobDescriptionDbModel,
  ) => {
    // Theme and jobToSave params are provided when called directly from
    // handleGenerate (state updates are async so we pass them explicitly)
    const resolvedTheme = theme ?? inheritedTheme;
    const resolvedJob = jobToSave ?? generatedJob;
    const validationErrors = validateResumePayload(value);
    if (validationErrors.summary.length > 0) {
      setError(
        `Fix these resume format issues: ${validationErrors.summary.join(' ')}`,
      );
      return;
    }

    try {
      setGenerating(true);
      setError(undefined);
      const response = await axios.post<ResumeDbModel>('/api/resumes', value);
      const created = response.data;
      try {
        if (created?.id && resolvedTheme) {
          localStorage.setItem(`resume_theme_${created.id}`, resolvedTheme);
        }
      } catch {}

      try {
        void dispatch(setHasResume(true));
      } catch {}

      if (saveGeneratedJob && resolvedJob) {
        try {
          await axios.post<JobDescriptionDbModel>('/api/jobs', resolvedJob);
          void dispatch(setHasJob(true));
        } catch (error_: unknown) {
          console.warn(
            'Failed to save generated job:',
            getErrorMessage(error_, 'Unknown error'),
          );
        }
      }

      onHide();
      if (created?.id === undefined || created?.id === null) {
        void navigate('/resumes');
      } else {
        void navigate(`/resumes/${created.id}`);
      }
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to save resume'));
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerate = async () => {
    if (!jobText) {
      setError('No job text provided');
      return;
    }

    if (!selectedResumeId) {
      setError('Please select an existing resume');
      return;
    }

    saveSettings();

    try {
      setGenerating(true);
      setError(undefined);

      const resumeResponse = await axios.get<ResumeDbModel>(
        `/api/resumes/${selectedResumeId}`,
      );
      const resumeModel = resumeResponse.data;

      // Inherit theme from base resume — compute locally so it's available
      // synchronously regardless of whether reviewBeforeSaving is on or off
      let resolvedTheme: string | undefined;
      try {
        const key = `resume_theme_${selectedResumeId}`;
        const saved =
          localStorage.getItem(key) ??
          localStorage.getItem('resume_theme_global');
        if (isStringOption(saved, resumeThemesNew)) {
          resolvedTheme = saved;
        }
      } catch {}

      setInheritedTheme(resolvedTheme);

      const response = await axios.post<AiResumeFromJobTextResponse>(
        '/api/ai/resume_from_jobtext_and_resume',
        {resume_model: resumeModel, job_input: {job_text: jobText}},
        {params: {strictness}},
      );
      const generatedResume = response.data.resume;
      const generatedJobData = response.data.job;
      setGeneratedJob(generatedJobData);
      track('ai_resume_created', {method: 'jobtext+resume', strictness});
      try {
        void dispatch(setHasTailored(true));
      } catch {}

      if (reviewBeforeSaving) {
        setGenerated(generatedResume);
      } else {
        await handleSave(generatedResume, resolvedTheme, generatedJobData);
      }
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to generate resume'));
    } finally {
      setGenerating(false);
    }
  };

  const handleDiscard = () => {
    setGenerated(undefined);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>Generate Resume from Job Text</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant='danger'>{error}</Alert>}

        {!generated && (
          <>
            <Row className='mb-3'>
              <Col>
                <Form.Label>
                  Select an existing resume to use as a base
                </Form.Label>
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col>
                {loadingResumes ? (
                  <div className='d-flex align-items-center'>
                    <Spinner animation='border' size='sm' className='me-2' />
                    <span>Loading resumes...</span>
                  </div>
                ) : (
                  <Form.Select
                    value={selectedResumeId}
                    onChange={event => {
                      setSelectedResumeId(
                        (event.target as HTMLSelectElement).value,
                      );
                    }}
                  >
                    <option value=''>-- select a resume --</option>
                    {resumes.map(r => (
                      <option key={r.id ?? r.name} value={r.id ?? ''}>
                        {r.name ?? `Resume ${r.id ?? ''}`}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col md={6}>
                <Form.Label>Strictness</Form.Label>
                <Form.Select
                  value={strictness}
                  onChange={event => {
                    setStrictness((event.target as HTMLSelectElement).value);
                  }}
                >
                  {StrictnessOptions.map(s => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col>
                <Form.Check
                  type='checkbox'
                  id='review-before-saving'
                  label='Review generated resume before saving'
                  checked={reviewBeforeSaving}
                  onChange={event => {
                    setReviewBeforeSaving(
                      (event.target as HTMLInputElement).checked,
                    );
                  }}
                />
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col>
                <Form.Check
                  type='checkbox'
                  id='save-generated-job'
                  label='Also save the generated job'
                  checked={saveGeneratedJob}
                  onChange={event => {
                    setSaveGeneratedJob(
                      (event.target as HTMLInputElement).checked,
                    );
                  }}
                />
              </Col>
            </Row>

            <BasicAiAlert />

            <div className='d-flex justify-content-end'>
              <Button
                variant='secondary'
                className='me-2'
                onClick={onHide}
                disabled={generating}
              >
                Cancel
              </Button>
              <Button
                disabled={generating}
                onClick={() => {
                  void handleGenerate();
                }}
              >
                {generating ? 'Generating...' : 'Generate'}
              </Button>
              {generating && <Spinner animation='border' className='ms-2' />}
            </div>
          </>
        )}

        {generated && (
          <>
            <Alert variant='info'>
              AI-generated resume — review and edit before saving.
            </Alert>
            <ResumeForm
              value={generated}
              onChange={v => {
                setGenerated(v);
              }}
              onSubmit={v => {
                void handleSave(v);
              }}
              readOnly={false}
              showActions={true}
              showTopActions={true}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {generated ? (
          <>
            <Button
              variant='outline-secondary'
              onClick={() => {
                setGenerated(undefined);
              }}
              disabled={generating}
            >
              Back
            </Button>
            <Button
              variant='secondary'
              onClick={handleDiscard}
              disabled={generating}
            >
              Discard
            </Button>
          </>
        ) : (
          <Button variant='secondary' onClick={onHide} disabled={generating}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

function MobileRowMenu({
  onEdit,
  onView,
  onGenerateResume,
  onExportJson,
  onDelete,
  disabled,
}: {
  onEdit?: () => void;
  onView?: () => void;
  onGenerateResume?: () => void | Promise<void>;
  onExportJson?: () => void;
  onDelete?: () => void;
  disabled?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const {refs, floatingStyles, context} = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), shift({padding: 8})],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, {role: 'menu'});
  const {getReferenceProps, getFloatingProps} = useInteractions([
    click,
    dismiss,
    role,
  ]);

  return (
    <div className='d-flex d-md-none justify-content-end'>
      <button
        type='button'
        className='btn btn-secondary btn-sm'
        ref={refs.setReference}
        {...getReferenceProps()}
        disabled={disabled}
        aria-haspopup='menu'
        aria-expanded={open}
      >
        <FaEllipsisV />
      </button>

      {open ? (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                zIndex: 2000,
                display: 'inline-block',
                width: 'auto',
                minWidth: 'auto',
                maxWidth: '80vw',
                overflow: 'hidden',
              }}
              className='dropdown-menu show'
              {...getFloatingProps()}
            >
              {onEdit ? (
                <button
                  className='dropdown-item icon-btn flex-nowrap'
                  onClick={() => {
                    onEdit();
                    setOpen(false);
                  }}
                >
                  <FaEdit size={18} />
                  Edit
                </button>
              ) : null}
              {onView ? (
                <button
                  className='dropdown-item icon-btn flex-nowrap'
                  onClick={() => {
                    onView();
                    setOpen(false);
                  }}
                >
                  <FaFilePdf size={14} />
                  View/Export PDF
                </button>
              ) : null}
              {onGenerateResume ? (
                <button
                  className='dropdown-item d-inline-flex align-items-center flex-nowrap'
                  onClick={() => {
                    void onGenerateResume();
                    setOpen(false);
                  }}
                >
                  <VscSparkleFilled size={18} className='me-2' />
                  Generate Resume
                </button>
              ) : null}
              {onExportJson ? (
                <button
                  className='dropdown-item icon-btn flex-nowrap'
                  onClick={() => {
                    onExportJson();
                    setOpen(false);
                  }}
                >
                  <LuFileJson size={16} />
                  Export JSON
                </button>
              ) : null}
              {onDelete ? (
                <>
                  <div className='dropdown-divider' />
                  <button
                    className='dropdown-item text-danger icon-btn flex-nowrap'
                    onClick={() => {
                      onDelete();
                      setOpen(false);
                    }}
                  >
                    <FaTrash size={18} />
                    Delete
                  </button>
                </>
              ) : null}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </div>
  );
}

function JobsList(): React.JSX.Element {
  const [items, setItems] = React.useState<JobDescriptionListInfo[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [showGenerateModal, setShowGenerateModal] = React.useState(false);
  const [selectedJob, setSelectedJob] = React.useState<
    JobDescriptionDbModel | undefined
  >(undefined);
  const [showImportModal, setShowImportModal] = React.useState(false);
  const [importText, setImportText] = React.useState('');
  const [importError, setImportError] = React.useState<string | undefined>(
    undefined,
  );
  const [importModel, setImportModel] = React.useState<
    JobDescriptionDbModel | undefined
  >(undefined);
  const [importSaving, setImportSaving] = React.useState(false);
  const [exportingJsonId, setExportingJsonId] = React.useState<
    string | undefined
  >(undefined);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [page, setPage] = React.useState<number>(() => {
    const sp = Number(searchParameters.get('page') ?? '1');
    return Number.isFinite(sp) && sp > 0 ? sp : 1;
  });
  const [perPage, setPerPage] = React.useState<number>(() => {
    const pp = Number(searchParameters.get('per_page') ?? '20');
    return Number.isFinite(pp) && pp > 0 ? pp : 20;
  });

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      const response = await axios.get<JobDescriptionListInfo[]>('/api/jobs', {
        params: {page, per_page: perPage},
      });
      setItems(response.data ?? []);
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to load jobs'));
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    setSearchParameters({page: String(page), per_page: String(perPage)});
  }, [page, perPage, setSearchParameters]);

  const handleDelete = async (id?: string) => {
    if (!id) {
      return;
    }

    try {
      await axios.delete(`/api/jobs/${id}`);
      setItems(previous => previous.filter(it => it.id !== id));
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to delete job'));
    }
  };

  const handleExportJson = async (id?: string) => {
    if (!id) {
      return;
    }

    try {
      setExportingJsonId(id);
      setError(undefined);
      const response = await axios.get<JobDescriptionDbModel>(
        `/api/jobs/${id}`,
      );
      const full = response.data;
      if (!full) {
        setError('Job not found');
        return;
      }

      const jobOnly: JobDescriptionSchema = full.job ?? {};
      const json = JSON.stringify(jobOnly, null, 2);
      const blob = new Blob([json], {type: 'application/json'});
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (full.name ?? 'job').replaceAll(/[^\w\-.]/gi, '_');
      a.download = `${safeName}.json`;
      document.body.append(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to export JSON'));
    } finally {
      setExportingJsonId(undefined);
    }
  };

  const openImportModal = () => {
    setImportText('');
    setImportError(undefined);
    setImportModel(undefined);
    setShowImportModal(true);
  };

  const handleImportFile = async (file?: File) => {
    if (!file) {
      return;
    }

    try {
      setImportText(await file.text());
    } catch {
      setImportError('Failed to read file');
    }
  };

  const parseImport = () => {
    try {
      setImportError(undefined);
      const parsed = JSON.parse(importText) as unknown;
      if (!isRecord(parsed)) {
        throw new Error('JSON must be an object');
      }

      if ('job' in parsed) {
        throw new Error(
          "Please provide a raw JobDescription JSON, not an object with a 'job' field.",
        );
      }

      const job: JobDescriptionSchema = parsed;
      const derivedName =
        typeof job.title === 'string'
          ? job.title
          : typeof job.company === 'string'
            ? job.company
            : 'imported';
      setImportModel({
        name: derivedName,
        url: '',
        id: '',
        user_id: '',
        job,
      });
    } catch (error_: unknown) {
      setImportError(getErrorMessage(error_, 'Invalid JSON'));
    }
  };

  const saveImport = async (v?: JobDescriptionDbModel) => {
    const payload = v ?? importModel;
    if (!payload) {
      return;
    }

    try {
      setImportSaving(true);
      await axios.post<JobDescriptionDbModel>('/api/jobs', payload);
      track('job_created', {via: 'import_json'});
      try {
        void dispatch(setHasJob(true));
      } catch {}

      // Success: reset and refresh list
      setShowImportModal(false);
      setImportModel(undefined);
      setImportText('');
      await load();
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to create job'));
    } finally {
      setImportSaving(false);
    }
  };

  const discardImport = () => {
    setImportModel(undefined);
    setImportText('');
    setImportError(undefined);
    setShowImportModal(false);
  };

  return (
    <Container className='my-5'>
      <Stack direction='horizontal' className='mb-3' gap={2}>
        <h1 className='mb-0'>Jobs</h1>
        <div className='ms-auto'>
          <div className='d-none d-md-flex align-items-center'>
            <Link to='new' className='btn btn-primary btn-sm me-2 icon-btn'>
              <FaPlusCircle size={16} />
              Create Job
            </Link>
            <Link to='ai' className='btn btn-secondary btn-sm me-2 icon-btn'>
              <VscSparkleFilled size={16} />
              Create Job w/ AI
            </Link>
            <Button
              variant='outline-secondary'
              className='btn-sm icon-btn'
              onClick={openImportModal}
            >
              <LuFileJson size={16} />
              Import JSON
            </Button>
          </div>

          <div className='d-flex d-md-none'>
            <Dropdown align='end'>
              <Dropdown.Toggle
                variant='secondary'
                size='sm'
                id='jobs-actions-dropdown'
              >
                Create
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item as={Link} to='new' className='icon-btn'>
                  <FaPlusCircle size={16} />
                  Create Job
                </Dropdown.Item>
                <Dropdown.Item as={Link} to='ai' className='icon-btn'>
                  <VscSparkleFilled size={16} /> Create Job w/ AI
                </Dropdown.Item>
                <Dropdown.Item className='icon-btn' onClick={openImportModal}>
                  <LuFileJson size={16} />
                  Import JSON
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </Stack>

      {loading && (
        <div className='d-flex align-items-center'>
          <Spinner animation='border' size='sm' className='me-2' />
          <span>Loading...</span>
        </div>
      )}
      {error && <Alert variant='danger'>{error}</Alert>}

      {!loading && !error && (
        <Table responsive striped bordered hover size='sm'>
          <thead>
            <tr>
              <th style={{width: '40%'}}>Name</th>
              <th style={{width: '60%'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={2}>No jobs found.</td>
              </tr>
            )}
            {items.map(it => (
              <tr key={it.id ?? it.name}>
                <td style={{whiteSpace: 'normal', wordBreak: 'break-word'}}>
                  {it.id ? <Link to={`${it.id}`}>{it.name}</Link> : it.name}
                </td>
                <td style={{whiteSpace: 'normal'}}>
                  <div className='d-none d-md-flex'>
                    <Stack
                      direction='horizontal'
                      gap={2}
                      style={{flexWrap: 'wrap', rowGap: 8}}
                    >
                      {it.id && (
                        <OverlayTrigger
                          placement='top'
                          delay={{show: 250, hide: 400}}
                          overlay={<Tooltip>Edit</Tooltip>}
                        >
                          <Link
                            to={`${it.id}`}
                            className='btn btn-outline-secondary btn-sm'
                          >
                            <FaEdit size={24} />
                          </Link>
                        </OverlayTrigger>
                      )}
                      <Button
                        variant='outline-primary'
                        size='sm'
                        onClick={() => {
                          if (it.id) {
                            void navigate(`preview/${it.id}`);
                          }
                        }}
                        disabled={!it.id}
                        className='icon-btn'
                      >
                        <FaFilePdf size={18} />
                        View/Export PDF
                      </Button>
                      <Button
                        variant='outline-primary'
                        size='sm'
                        onClick={() => {
                          void (async () => {
                            if (!it.id) {
                              return;
                            }

                            try {
                              const response =
                                await axios.get<JobDescriptionDbModel>(
                                  `/api/jobs/${it.id}`,
                                );
                              setSelectedJob(response.data);
                              setShowGenerateModal(true);
                            } catch (error_: unknown) {
                              setError(
                                getErrorMessage(error_, 'Failed to load job'),
                              );
                            }
                          })();
                        }}
                        disabled={!it.id}
                        className='icon-btn'
                      >
                        <VscSparkleFilled size={16} />
                        Generate Resume
                      </Button>
                      <Button
                        variant='outline-secondary'
                        size='sm'
                        onClick={() => {
                          void handleExportJson(it.id);
                        }}
                        disabled={!it.id}
                        className='icon-btn'
                      >
                        {exportingJsonId === it.id ? (
                          <>
                            <Spinner
                              animation='border'
                              size='sm'
                              className='me-2'
                            />
                            Exporting...
                          </>
                        ) : (
                          <>
                            <LuFileJson size={16} />
                            Export JSON
                          </>
                        )}
                      </Button>
                      <OverlayTrigger
                        placement='top'
                        delay={{show: 250, hide: 400}}
                        overlay={<Tooltip>Delete</Tooltip>}
                      >
                        <Button
                          variant='outline-danger'
                          size='sm'
                          onClick={() => {
                            void handleDelete(it.id);
                          }}
                          disabled={!it.id}
                        >
                          <FaTrash size={24} />
                        </Button>
                      </OverlayTrigger>
                    </Stack>
                  </div>

                  <MobileRowMenu
                    disabled={!it.id}
                    onEdit={
                      it.id
                        ? () => {
                            void navigate(`${it.id}`);
                          }
                        : undefined
                    }
                    onView={
                      it.id
                        ? () => {
                            void navigate(`preview/${it.id}`);
                          }
                        : undefined
                    }
                    onGenerateResume={
                      it.id
                        ? () => {
                            void (async () => {
                              try {
                                const response =
                                  await axios.get<JobDescriptionDbModel>(
                                    `/api/jobs/${it.id}`,
                                  );
                                setSelectedJob(response.data);
                                setShowGenerateModal(true);
                              } catch (error_: unknown) {
                                setError(
                                  getErrorMessage(error_, 'Failed to load job'),
                                );
                              }
                            })();
                          }
                        : undefined
                    }
                    onExportJson={
                      it.id
                        ? () => {
                            void handleExportJson(it.id);
                          }
                        : undefined
                    }
                    onDelete={
                      it.id
                        ? () => {
                            void handleDelete(it.id);
                          }
                        : undefined
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {!loading && !error && (
        <div className='d-flex align-items-center justify-content-between mt-2'>
          <div className='d-flex align-items-center'>
            <Form.Label className='me-2 mb-0'>Rows per page</Form.Label>
            <Form.Select
              size='sm'
              style={{width: 'auto'}}
              value={perPage}
              onChange={event => {
                const next = Number((event.target as HTMLSelectElement).value);
                setPerPage(next);
                setPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </div>
          <div className='d-flex align-items-center'>
            <span className='me-3'>Page {page}</span>
            <Pagination className='mb-0'>
              <Pagination.Prev
                onClick={() => {
                  setPage(p => Math.max(1, p - 1));
                }}
                disabled={page <= 1 || loading}
              />
              <Pagination.Next
                onClick={() => {
                  setPage(p => p + 1);
                }}
                disabled={loading || items.length < perPage}
              />
            </Pagination>
          </div>
        </div>
      )}
      <Modal
        show={showImportModal}
        onHide={() => {
          setShowImportModal(false);
          setImportModel(undefined);
        }}
        size='lg'
      >
        <Modal.Header closeButton>
          <Modal.Title>Import Job JSON</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!importModel && (
            <Form>
              <Form.Group controlId='importJobFile' className='mb-3'>
                <Form.Label>Choose JSON file (optional)</Form.Label>
                <Form.Control
                  type='file'
                  accept='application/json'
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const f = event.currentTarget.files?.[0];
                    if (f) {
                      void handleImportFile(f);
                    }
                  }}
                />
              </Form.Group>
              <Form.Group controlId='importJobText' className='mb-3'>
                <Form.Label>Or paste JobDescription JSON</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={12}
                  value={importText}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setImportText(event.currentTarget.value);
                  }}
                  placeholder='Paste raw JobDescription JSON here'
                />
              </Form.Group>
              {importError && <Alert variant='danger'>{importError}</Alert>}
            </Form>
          )}
          {importModel && (
            <div>
              <Alert variant='info'>
                Edit the imported job below. Save to post it, or discard to
                cancel.
              </Alert>
              <JobForm
                value={importModel}
                onChange={v => {
                  setImportModel(v);
                }}
                onSubmit={v => {
                  void saveImport(v);
                }}
                readOnly={false}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {importModel ? (
            <>
              <Button
                variant='secondary'
                onClick={discardImport}
                disabled={importSaving}
              >
                Discard
              </Button>
              <Button
                variant='primary'
                onClick={() => {
                  void saveImport(importModel);
                }}
                disabled={importSaving}
              >
                {importSaving ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant='secondary'
                onClick={() => {
                  setShowImportModal(false);
                  setImportText('');
                  setImportError(undefined);
                }}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={parseImport}
                disabled={!importText}
              >
                Parse JSON
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <GenerateResumeModal
        show={showGenerateModal}
        onHide={() => {
          setShowGenerateModal(false);
          setSelectedJob(undefined);
        }}
        job={selectedJob}
      />
    </Container>
  );
}

function JobEditor(): React.JSX.Element {
  const parameters = useParams();
  const navigate = useNavigate();
  const jobId = parameters.id;
  const [model, setModel] = React.useState<JobDescriptionDbModel | undefined>(
    undefined,
  );
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [saving, setSaving] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(true);
  const [isMobile, setIsMobile] = React.useState<boolean>(false);
  const [debouncedModel, setDebouncedModel] = React.useState<
    JobDescriptionDbModel | undefined
  >(undefined);
  const [showGenerateModal, setShowGenerateModal] = React.useState(false);

  // For exporting the live preview to PDF while editing
  const previewRef = React.useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = React.useState(false);
  const safeName = (s?: string) => (s ?? 'job').replaceAll(/[^\w.-]/gi, '_');

  const exportPdf = async (): Promise<void> => {
    const element = previewRef.current;
    const current = debouncedModel ?? model;
    if (!element || !current) {
      return;
    }

    setExporting(true);
    try {
      try {
        // Optional; not all browsers implement document.fonts
        await document.fonts?.ready;
      } catch {
        /* ignore */
      }

      // Build off-DOM wrapper with safe color overrides (avoid oklch() etc)
      const wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.left = '-10000px';
      wrapper.style.top = '0';
      wrapper.style.pointerEvents = 'none';

      const style = document.createElement('style');
      style.textContent = `
        .pdf-safe {
          --bs-body-color: #212529;
          --bs-body-bg: #ffffff;
          --bs-secondary-color: #6c757d;
          --bs-secondary-bg: #e9ecef;
          --bs-border-color: #dee2e6;
          --bs-primary: #0d6efd;
          --bs-light: #f8f9fa;
          --bs-dark: #212529;
          --bs-link-color: #0d6efd;
          --bs-link-hover-color: #0a58ca;
          color-scheme: light;
        }
        .pdf-safe .bg-white { background-color: #ffffff !important; }
        .pdf-safe .bg-light { background-color: #f8f9fa !important; }
        .pdf-safe .text-muted { color: #6c757d !important; }
        .pdf-safe .badge.bg-light {
          background-color: #f8f9fa !important;
          color: #212529 !important;
          border-color: #dee2e6 !important;
        }
        .pdf-safe a { color: #0d6efd !important; }
      `;
      wrapper.append(style);

      const clone = element.cloneNode(true);
      if (!(clone instanceof HTMLElement)) {
        throw new TypeError('Failed to clone element');
      }

      clone.classList.add('pdf-safe');
      wrapper.append(clone);
      document.body.append(wrapper);

      // Rasterize the cloned node to an image
      const dataUrl = await toPng(clone, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
      });

      // Cleanup wrapper
      wrapper.remove();

      // Create PDF and paginate if needed
      const doc = new JsPDF({unit: 'pt', format: 'a4'});
      const margin = 36; // 0.5in
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const usableHeight = pageHeight - margin * 2;

      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.addEventListener('load', () => {
          resolve();
        });
        img.addEventListener('error', () => {
          reject(new Error('Failed to load image'));
        });
        img.src = dataUrl;
      });

      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (img.naturalHeight / img.naturalWidth) * imgWidth;

      doc.addImage(dataUrl, 'PNG', margin, margin, imgWidth, imgHeight);

      let heightLeft = imgHeight - usableHeight;
      while (heightLeft > 0) {
        doc.addPage();
        const y = margin - (imgHeight - heightLeft);
        doc.addImage(dataUrl, 'PNG', margin, y, imgWidth, imgHeight);
        heightLeft -= usableHeight;
      }

      doc.save(`${safeName(current.name)}.pdf`);
    } catch (error_) {
      console.error('Failed to export job PDF:', error_);
    } finally {
      setExporting(false);
    }
  };

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedModel(model);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [model]);

  React.useEffect(() => {
    try {
      const mq = globalThis.matchMedia('(max-width: 767.98px)');
      const handler = (event: MediaQueryListEvent) => {
        setIsMobile(event.matches);
      };

      setIsMobile(mq.matches);
      mq.addEventListener('change', handler);

      return () => {
        mq.removeEventListener('change', handler);
      };
    } catch {
      // ignore (e.g., SSR)
    }
  }, []);

  React.useEffect(() => {
    if (!jobId) {
      setError('Invalid job id');
      setLoading(false);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(undefined);
        const response = await axios.get<JobDescriptionDbModel>(
          `/api/jobs/${jobId}`,
        );
        if (mounted) {
          setModel(response.data);
        }
      } catch (error_: unknown) {
        if (mounted) {
          setError(getErrorMessage(error_, 'Failed to load job'));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [jobId]);

  const handleSubmit = async (v: JobDescriptionDbModel) => {
    if (!jobId) {
      return;
    }

    try {
      setSaving(true);
      await axios.put(`/api/jobs/${jobId}`, {...v, id: jobId});
      void navigate('/jobs');
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to save job'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!jobId) {
      return;
    }

    // eslint-disable-next-line no-alert
    if (!globalThis.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      await axios.delete(`/api/jobs/${jobId}`);
      void navigate('/jobs');
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to delete job'));
    }
  };

  return (
    <div className={showPreview && !isMobile ? 'my-5 mx-5' : 'my-5 container'}>
      <h2 className='mb-3'>Edit Job</h2>
      <Stack direction='horizontal' className='mb-3' gap={2}>
        <Button
          variant='outline-secondary'
          onClick={() => {
            void navigate('/jobs');
          }}
        >
          Back
        </Button>
        <div className='ms-auto d-flex align-items-center'>
          {isMobile ? (
            <Dropdown align='end'>
              <Dropdown.Toggle
                variant='secondary'
                size='sm'
                id='job-actions-dropdown'
              >
                Actions
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => {
                    setShowGenerateModal(true);
                  }}
                  disabled={loading || !model}
                >
                  <VscSparkleFilled size={16} className='icon-btn me-2' />
                  Generate Resume
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    if (jobId) {
                      void navigate(`/jobs/preview/${jobId}`);
                    }
                  }}
                >
                  <FaFilePdf size={14} className='icon-btn me-2' />
                  View/Export PDF
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item
                  onClick={() => {
                    void handleDelete();
                  }}
                  className='text-danger'
                >
                  <FaTrash size={14} className='icon-btn me-2' />
                  Delete Job
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <>
              <Button
                variant='outline-primary'
                onClick={() => {
                  setShowGenerateModal(true);
                }}
                size={isMobile ? 'sm' : undefined}
                className='icon-btn me-2'
                disabled={loading || !model}
              >
                <VscSparkleFilled size={16} className='me-1' />
                <span className='d-none d-md-inline'>Generate Resume</span>
                <span className='d-inline d-md-none'>Generate</span>
              </Button>
              <Button
                variant={showPreview ? 'primary' : 'outline-secondary'}
                onClick={() => {
                  setShowPreview(s => !s);
                }}
                className='me-2'
              >
                {showPreview ? 'Hide Preview' : 'Show Preview'}
              </Button>
              <Button
                variant='outline-danger'
                onClick={() => {
                  void handleDelete();
                }}
                className='me-2'
              >
                <FaTrash size={14} className='icon-btn me-2' />
                Delete
              </Button>
            </>
          )}
        </div>
      </Stack>
      {loading && (
        <div className='d-flex align-items-center'>
          <Spinner animation='border' size='sm' className='me-2' />
          <span>Loading...</span>
        </div>
      )}
      {error && <Alert variant='danger'>{error}</Alert>}
      {!loading && !error && model && (
        <>
          {showPreview && !isMobile ? (
            <ResizableSplit initialLeftPercent={55}>
              <div
                key='left'
                className='mb-3'
                style={{
                  paddingRight: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'calc(100vh - 220px)',
                  overflow: 'auto',
                }}
              >
                <JobForm
                  value={model}
                  onChange={v => {
                    setModel(v);
                  }}
                  onSubmit={v => {
                    void handleSubmit(v);
                  }}
                  readOnly={false}
                />
              </div>

              <div
                key='right'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'calc(100vh - 220px)',
                }}
              >
                <Stack direction='horizontal' className='mb-3' gap={2}>
                  <h5 className='mb-0'>Preview</h5>
                  <div className='ms-auto'>
                    <Button
                      size='sm'
                      variant='primary'
                      onClick={() => {
                        void exportPdf();
                      }}
                      disabled={exporting}
                    >
                      {exporting ? 'Generating...' : 'Download PDF'}
                    </Button>
                  </div>
                </Stack>
                <div
                  style={{
                    borderLeft: '1px solid #e9ecef',
                    paddingLeft: '1rem',
                    overflow: 'auto',
                    flex: 1,
                  }}
                >
                  <div ref={previewRef}>
                    <JobPreview model={debouncedModel ?? model} />
                  </div>
                </div>
              </div>
            </ResizableSplit>
          ) : (
            <JobForm
              value={model}
              onChange={v => {
                setModel(v);
              }}
              onSubmit={v => {
                void handleSubmit(v);
              }}
              readOnly={false}
            />
          )}
        </>
      )}
      {saving && (
        <div className='mt-2'>
          <Spinner animation='grow' size='sm' className='me-2' />
          <span>Saving...</span>
        </div>
      )}
      <GenerateResumeModal
        show={showGenerateModal}
        onHide={() => {
          setShowGenerateModal(false);
        }}
        job={model}
      />
    </div>
  );
}

function JobCreate(): React.JSX.Element {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [model, setModel] = React.useState<JobDescriptionDbModel>({
    name: '',
    id: '',
    user_id: '',
    job: {},
  });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);

  const handleSubmit = async (v: JobDescriptionDbModel) => {
    try {
      setSaving(true);
      setError(undefined);
      const response = await axios.post<JobDescriptionDbModel>('/api/jobs', v);
      const created = response.data;
      track('job_created', {id: created?.id});
      try {
        dispatch(setHasJob(true));
      } catch {}

      if (created?.id === undefined || created?.id === null) {
        void navigate('/jobs');
      } else {
        void navigate(`/jobs/${created.id}`);
      }
    } catch (error: unknown) {
      setError(getErrorMessage(error, 'Failed to create job'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container className='my-5'>
      <Stack direction='horizontal' className='mb-3' gap={2}>
        <h2 className='mb-0'>Create Job</h2>
        <div className='ms-auto'>
          <Button
            variant='outline-secondary'
            onClick={() => {
              void navigate('/jobs');
            }}
          >
            Back
          </Button>
        </div>
      </Stack>
      {error && <Alert variant='danger'>{error}</Alert>}
      <JobForm
        value={model}
        onChange={v => {
          setModel(v);
        }}
        onSubmit={v => {
          void handleSubmit(v);
        }}
        readOnly={false}
      />
      {saving && (
        <div className='mt-2'>
          <Spinner animation='grow' size='sm' className='me-2' />
          <span>Creating...</span>
        </div>
      )}
    </Container>
  );
}

function AiJobBuilder(): React.JSX.Element {
  const [input, setInput] = React.useState<string>('');
  const [result, setResult] = React.useState<JobDescriptionDbModel | undefined>(
    undefined,
  );
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [showGenerateResumeModal, setShowGenerateResumeModal] =
    React.useState(false);
  const navigate = useNavigate();

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError(undefined);
      const response = await axios.post<JobDescriptionDbModel>('/api/ai/jobs', {
        job_text: input.normalize('NFC'),
      });
      setResult(response.data);
      track('ai_job_generated');
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to generate AI job'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (v: JobDescriptionDbModel) => {
    try {
      setLoading(true);
      const response = await axios.post<JobDescriptionDbModel>('/api/jobs', v);
      const created = response.data;
      if (created?.id === undefined || created?.id === null) {
        void navigate('/jobs');
      } else {
        void navigate(`/jobs/${created.id}`);
      }
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to save job'));
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = () => {
    setResult(undefined);
    setInput('');
    void navigate('/jobs');
  };

  return (
    <Container className='my-5'>
      <Stack direction='horizontal' className='mb-3' gap={2}>
        <h2 className='mb-0'>AI Job Builder</h2>
        <div className='ms-auto'>
          <Button
            variant='outline-secondary'
            onClick={() => {
              void navigate('/jobs');
            }}
          >
            Back
          </Button>
        </div>
      </Stack>

      {!result && (
        <>
          {error && <Alert variant='danger'>{error}</Alert>}
          <Form>
            <Form.Group className='mb-3' controlId='aiJobText'>
              <Form.Label>Paste job posting text</Form.Label>
              <Form.Control
                as='textarea'
                rows={8}
                value={input}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                  setInput(event.currentTarget.value);
                }}
                placeholder='Paste job posting content here...'
              />
              <BasicAiAlert />
            </Form.Group>
            <div className='d-flex align-items-center'>
              <Button
                onClick={() => {
                  void handleGenerate();
                }}
                disabled={!input || loading}
                className='icon-btn'
              >
                {loading ? (
                  'Generating...'
                ) : (
                  <>
                    <VscSparkleFilled size={16} /> Generate AI Job
                  </>
                )}
              </Button>
              <Button
                variant='outline-secondary'
                className='ms-2 icon-btn'
                onClick={() => {
                  setShowGenerateResumeModal(true);
                }}
                disabled={!input || loading}
              >
                <VscSparkleFilled size={16} /> Generate Resume
              </Button>
              {loading && (
                <Spinner animation='border' size='sm' className='ms-2' />
              )}
            </div>
          </Form>
        </>
      )}

      {result && (
        <>
          <Alert variant='info'>
            AI job generated — you can edit the result below. Save to persist or
            discard to cancel.
          </Alert>
          <JobForm
            value={result}
            onChange={v => {
              setResult(v);
            }}
            onSubmit={v => {
              void handleSave(v);
            }}
            readOnly={false}
            showTopActions={true}
          />
          <div className='d-flex justify-content-end mt-2'>
            <Button
              variant='outline-secondary'
              className='me-2'
              onClick={handleDiscard}
            >
              Discard
            </Button>
          </div>
        </>
      )}

      <GenerateResumeFromJobTextModal
        show={showGenerateResumeModal}
        onHide={() => {
          setShowGenerateResumeModal(false);
        }}
        jobText={input}
      />
    </Container>
  );
}

function JobPreviewWrapper(): React.JSX.Element {
  const parameters = useParams();
  const jobId = parameters.id;
  const navigate = useNavigate();
  const [model, setModel] = React.useState<JobDescriptionDbModel | undefined>(
    undefined,
  );
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | undefined>(undefined);

  const previewRef = React.useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = React.useState(false);
  const [showGenerateModal, setShowGenerateModal] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    try {
      const mq = globalThis.matchMedia('(max-width: 767.98px)');
      const handler = (event: MediaQueryListEvent) => {
        setIsMobile(event.matches);
      };

      setIsMobile(mq.matches);
      mq.addEventListener('change', handler);

      return () => {
        mq.removeEventListener('change', handler);
      };
    } catch {
      // ignore (SSR)
    }
  }, []);

  const safeName = (s?: string) => (s ?? 'job').replaceAll(/[^\w.-]/gi, '_');

  const exportPdf = async (): Promise<void> => {
    const element = previewRef.current;
    if (!element || !model) {
      return;
    }

    setExporting(true);
    try {
      // Ensure fonts are ready before rasterizing
      try {
        // Optional; not all browsers implement document.fonts
        await document.fonts?.ready;
      } catch {
        /* ignore */
      }

      // Build an off-DOM wrapper with safe color overrides to avoid unsupported CSS color functions
      const wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.left = '-10000px';
      wrapper.style.top = '0';
      wrapper.style.pointerEvents = 'none';

      const style = document.createElement('style');
      style.textContent = `
        .pdf-safe {
          --bs-body-color: #212529;
          --bs-body-bg: #ffffff;
          --bs-secondary-color: #6c757d;
          --bs-secondary-bg: #e9ecef;
          --bs-border-color: #dee2e6;
          --bs-primary: #0d6efd;
          --bs-light: #f8f9fa;
          --bs-dark: #212529;
          --bs-link-color: #0d6efd;
          --bs-link-hover-color: #0a58ca;
          color-scheme: light;
        }
        .pdf-safe .bg-white { background-color: #ffffff !important; }
        .pdf-safe .bg-light { background-color: #f8f9fa !important; }
        .pdf-safe .text-muted { color: #6c757d !important; }
        .pdf-safe .badge.bg-light {
          background-color: #f8f9fa !important;
          color: #212529 !important;
          border-color: #dee2e6 !important;
        }
        .pdf-safe a { color: #0d6efd !important; }
      `;
      wrapper.append(style);

      const clone = element.cloneNode(true);
      if (!(clone instanceof HTMLElement)) {
        throw new TypeError('Failed to clone element');
      }

      clone.classList.add('pdf-safe');
      wrapper.append(clone);
      document.body.append(wrapper);

      // Render the cloned node to an image (higher pixelRatio for sharper text)
      const dataUrl = await toPng(clone, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
      });

      // Cleanup the temporary wrapper
      wrapper.remove();

      // Create PDF and paginate if necessary
      const doc = new JsPDF({unit: 'pt', format: 'a4'});
      const margin = 36; // 0.5in margins
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const usableHeight = pageHeight - margin * 2;

      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.addEventListener('load', () => {
          resolve();
        });
        img.addEventListener('error', () => {
          reject(new Error('Failed to load image'));
        });
        img.src = dataUrl;
      });

      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (img.naturalHeight / img.naturalWidth) * imgWidth;

      // First page
      doc.addImage(dataUrl, 'PNG', margin, margin, imgWidth, imgHeight);

      // Additional pages (shift same tall image upward; jsPDF clips per page)
      let heightLeft = imgHeight - usableHeight;
      while (heightLeft > 0) {
        doc.addPage();
        const y = margin - (imgHeight - heightLeft);
        doc.addImage(dataUrl, 'PNG', margin, y, imgWidth, imgHeight);
        heightLeft -= usableHeight;
      }

      doc.save(`${safeName(model.name)}.pdf`);
    } catch (error_) {
      console.error('Failed to export job PDF:', error_);
    } finally {
      setExporting(false);
    }
  };

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      if (!jobId) {
        if (mounted) {
          setError('Invalid job id');
          setLoading(false);
        }

        return;
      }

      try {
        setLoading(true);
        setError(undefined);
        const response = await axios.get<JobDescriptionDbModel>(
          `/api/jobs/${jobId}`,
        );
        if (mounted) {
          setModel(response.data);
        }
      } catch (error_: unknown) {
        if (mounted) {
          setError(getErrorMessage(error_, 'Failed to load job'));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [jobId]);

  if (loading) {
    return (
      <Container className='my-5 d-flex align-items-center'>
        <Spinner animation='border' />
        <span className='ms-2'>Loading preview...</span>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className='my-5'>
        <Alert variant='danger'>{error}</Alert>
      </Container>
    );
  }

  if (!model) {
    return (
      <Container className='my-5'>
        <Alert variant='warning'>Job not found</Alert>
      </Container>
    );
  }

  return (
    <Container className='my-5'>
      <Stack direction='horizontal' className='mb-3' gap={2}>
        <Button
          variant='outline-secondary'
          onClick={() => {
            void navigate('/jobs');
          }}
        >
          Back
        </Button>
        <div className='ms-auto'>
          {isMobile ? (
            <Dropdown align='end'>
              <Dropdown.Toggle
                variant='secondary'
                size='sm'
                id='job-preview-actions-dropdown'
              >
                Actions
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => {
                    setShowGenerateModal(true);
                  }}
                  disabled={!model || exporting}
                >
                  <VscSparkleFilled size={16} className='icon-btn me-2' />
                  Generate Resume
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    if (!jobId) {
                      return;
                    }

                    void navigate(`/jobs/${jobId}`);
                  }}
                  disabled={!model || exporting}
                >
                  <FaEdit size={14} className='icon-btn me-2' />
                  Edit Job
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item
                  onClick={() => {
                    void exportPdf();
                  }}
                  disabled={!model || exporting}
                >
                  <FaFilePdf size={14} className='icon-btn me-2' />
                  {exporting ? 'Generating...' : 'Download PDF'}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <div className='d-flex align-items-center'>
              <Button
                variant='outline-primary'
                className='icon-btn me-2'
                onClick={() => {
                  setShowGenerateModal(true);
                }}
                disabled={!model || exporting}
              >
                <VscSparkleFilled size={16} className='me-1' />
                <span className='d-none d-md-inline'>Generate Resume</span>
                <span className='d-inline d-md-none'>Generate</span>
              </Button>
              <Button
                variant='outline-secondary'
                className='icon-btn me-2'
                onClick={() => {
                  if (!jobId) {
                    return;
                  }

                  void navigate(`/jobs/${jobId}`);
                }}
                disabled={!model || exporting}
              >
                <FaEdit size={14} className='icon-btn me-2' />
                <span className='d-none d-md-inline'>Edit Job</span>
                <span className='d-inline d-md-none'>Edit</span>
              </Button>
              <Button
                variant='primary'
                onClick={() => {
                  void exportPdf();
                }}
                disabled={exporting}
              >
                {exporting ? 'Generating...' : 'Download PDF'}
              </Button>
            </div>
          )}
        </div>
      </Stack>
      <div ref={previewRef}>
        <JobPreview model={model} />
      </div>
      <GenerateResumeModal
        show={showGenerateModal}
        onHide={() => {
          setShowGenerateModal(false);
        }}
        job={model}
      />
    </Container>
  );
}

const JobPreview = React.memo(
  ({model}: {model: JobDescriptionDbModel}): React.JSX.Element => {
    const job = model.job ?? {};

    const loc = job.location
      ? [job.location.city, job.location.region, job.location.countryCode]
          .filter(Boolean)
          .join(', ')
      : '';

    return (
      <div className='p-3' style={{background: '#f5f6f8'}}>
        <div
          className='mx-auto bg-white p-4 shadow-sm'
          style={{maxWidth: 900, borderRadius: 6}}
        >
          <div className='mb-3'>
            <h3 className='mb-1'>
              {job.title ?? model.name ?? 'Untitled Job'}
            </h3>
            <div className='text-muted'>
              {job.company ? <span>{job.company}</span> : null}
              {job.company && (Boolean(loc) || Boolean(job.remote)) ? (
                <span> · </span>
              ) : null}
              {loc ? <span>{loc}</span> : null}
              {loc && job.remote ? <span> · </span> : null}
              {job.remote ? <span>{job.remote}</span> : null}
            </div>
            <div className='text-muted small mt-1'>
              {job.type ? <span>{job.type}</span> : null}
              {job.type && job.salary ? <span> · </span> : null}
              {job.salary ? <span>{job.salary}</span> : null}
              {job.salary && job.experience ? <span> · </span> : null}
              {job.experience ? <span>{job.experience}</span> : null}
              {job.date ? <span> · {job.date}</span> : null}
            </div>
          </div>

          {job.description ? (
            <>
              <h5>Description</h5>
              <p style={{whiteSpace: 'pre-wrap'}}>{job.description}</p>
            </>
          ) : null}

          {Array.isArray(job.responsibilities) &&
          job.responsibilities.length > 0 ? (
            <>
              <h5>Responsibilities</h5>
              <ul>
                {job.responsibilities.map((r, i) => (
                  <li key={`resp-${i}`}>{r}</li>
                ))}
              </ul>
            </>
          ) : null}

          {Array.isArray(job.qualifications) &&
          job.qualifications.length > 0 ? (
            <>
              <h5>Qualifications</h5>
              <ul>
                {job.qualifications.map((q, i) => (
                  <li key={`qual-${i}`}>{q}</li>
                ))}
              </ul>
            </>
          ) : null}

          {Array.isArray(job.skills) && job.skills.length > 0 ? (
            <>
              <h5>Skills</h5>
              <div className='d-flex flex-column' style={{gap: 8}}>
                {job.skills.map((s, i) => (
                  <div key={`skill-${i}`}>
                    <span className='fw-semibold'>
                      {s?.name ?? 'Skill'}
                      {s?.level ? (
                        <span className='text-muted ms-1'>({s.level})</span>
                      ) : null}
                    </span>
                    {Array.isArray(s?.keywords) && s.keywords.length > 0 && (
                      <div className='mt-1 d-flex flex-wrap' style={{gap: 6}}>
                        {(s.keywords ?? []).map((k, j) => (
                          <span
                            key={`skill-${i}-kw-${j}`}
                            className='badge bg-light text-dark border'
                          >
                            {k}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {model.url ? (
            <div className='mt-3'>
              <a href={model.url} target='_blank' rel='noreferrer'>
                View original posting
              </a>
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

function JobsPage(): React.JSX.Element {
  return (
    <div>
      <Routes>
        <Route index element={<JobsList />} />
        <Route path='new' element={<JobCreate />} />
        <Route path='ai' element={<AiJobBuilder />} />
        <Route path='preview/:id' element={<JobPreviewWrapper />} />
        <Route path=':id' element={<JobEditor />} />
      </Routes>
    </div>
  );
}

export default JobsPage;
